/**
 * ComposeRecipientHeader Component
 * Displays recipient avatar, name for frontend context
 * Mirrors the ContactUserDialog header style
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { TYPOGRAPHY } from '@/design-system';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { User } from 'lucide-react';
import type { Expert } from './types';
import { getExpertDisplayName } from './types';

interface ComposeRecipientHeaderProps {
  recipients: Expert[];
  compact?: boolean;
}

export function ComposeRecipientHeader({ recipients, compact = false }: ComposeRecipientHeaderProps) {
  const [avatarError, setAvatarError] = useState(false);

  if (recipients.length === 0) return null;

  const primaryRecipient = recipients[0];
  const displayName = getExpertDisplayName(primaryRecipient);
  const fallbackInitial = (displayName || 'E')[0].toUpperCase();
  const additionalCount = recipients.length - 1;

  const avatarSize = compact ? 'w-8 h-8' : 'w-12 h-12';
  const iconSize = compact ? 'w-4 h-4' : 'w-6 h-6';

  return (
    <div className="flex items-center gap-3">
      <Avatar className={cn(avatarSize, 'flex-shrink-0')}>
        {primaryRecipient.image && !avatarError ? (
          <AvatarImage 
            src={primaryRecipient.image} 
            alt={displayName}
            onError={() => setAvatarError(true)}
          />
        ) : null}
        <AvatarFallback className="bg-primary/10 text-primary">
          {primaryRecipient.image && !avatarError ? null : (
            fallbackInitial !== 'E' ? fallbackInitial : <User className={iconSize} />
          )}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <span className={cn(compact ? 'text-sm' : TYPOGRAPHY.cardTitle, 'truncate font-medium block')}>
          {displayName}
          {additionalCount > 0 && (
            <span className="text-muted-foreground font-normal">
              {' '}+{additionalCount}
            </span>
          )}
        </span>
      </div>
    </div>
  );
}
